import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { CareMapState } from '../caremap/CareMapState';
import { ICareMap, ICareMapKey } from '../caremap/ICareMap';
import { IAcceptedCareMapInvite, IPendingCareMapInvite } from '../caremap/ICareMapInvite';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { ISO8601 } from '../support/ISO8601';

export type APIUrl = string;

const invitationAcceptanceHeaders = { 'content-type': 'application/vnd.innerhive.invitation-acceptance.v2+json' };

export type ICollaboratorPermission = {
    access: 'co-owner' | 'view-only';
    careMapKey: ICareMapKey;
    consentedAt: ISO8601;
    consentedBy: { userId: string; email: string; };
    invite: IAcceptedCareMapInvite;
    links: {
        self: {
            href: string;
        }
    };
    userId: string;

    // Server-side projection of the collaborator's current profile (Task 11). Populated
    // when the permission is fetched after Task 11 deploys. Used as a fallback when the
    // collaborator is not linked to a local care-map member node.
    collaborator?: {
        name: string;
        pictureUrl?: string;
        avatar?: string;
        color?: string;
    };
}

export interface ICollaborators {
    // even if the owner has been deleted this object will still exist (with only a userId)
    owner: {
        userId: string;

        // may be empty string (eg user was deleted)
        email: string;

        // may be empty string (eg user was deleted)
        name: string;
    };
    permissions: ICollaboratorPermission[];
};

export interface ICollaboratorCount {
    count: Number
};

export class CommunityClient {
    static inject = () => [
        APIClient,
        CareMapState,
        BufferedEventBus
    ];

    constructor(
        private api: APIClient,
        private careMapState: CareMapState,
        private events: BufferedEventBus
    ) { }

    validateInvite = (invite?: APIUrl) => {
        if (!invite){
            throw new Error(`/accept-invite, invite is required`);
        };
    };

    private postAcceptOrDecline = async (invite: APIUrl, consented: boolean): Promise<any> => {
        return await this.api.post(
            `/invites/${encodeURIComponent(invite as string)}/accept`,
            { consent: consented },
            invitationAcceptanceHeaders
        );
    };

    acceptedInvite = async (invite: APIUrl): Promise<{ careMap: ICareMap; careMapCategory: 'Inner Circle' | 'Community' | 'None' }> => {
        const { careMapKey: { careMapId, userId }, memberId } = await this.postAcceptOrDecline(invite, true);

        await this.careMapState.loadWithoutActive();
        const careMap = this.careMapState.getCareMaps().find(c => c.userId === userId && c.careMapId === careMapId);

        if (!careMap){
            throw new Error('after accepting an invite, expected to see the caremap, but do not (should never happen)');
        }

        this.events.emit('InvitationService.accepted');
        return { careMap, careMapCategory: this.careMapState.getMemberCareMapCategory(careMap, memberId) };
    };

    declinedInvite = async (invite: APIUrl) => {
        await this.postAcceptOrDecline(invite, false);
    };

    getInvites = async (caremap: ICareMap) => {
        return await this.api.get(caremap.links.invites);
    };

    getCollaboratorCount = async (caremap: ICareMap): Promise<ICollaboratorCount> => {
        return await this.api.get(caremap.links.collaboratorCount, 'application/vnd.innerhive.caremap-permission.v2+json');
    };

    getCollaborators = async (caremap: ICareMap): Promise<ICollaborators> => {
        return await this.api.get(caremap.links.permissions, 'application/vnd.innerhive.caremap-permission.v2+json');
    };

    delete = async (item: ICollaboratorPermission | IPendingCareMapInvite) => {
        await this.api.delete(item.links.self.href);
    };
};
